import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as echarts from 'echarts';
import { NgxEchartsModule } from 'ngx-echarts';
import * as ecStatRaw from 'echarts-stat';
import { buildConfigurableTooltipFormatter, formatTooltipValue, getTooltipLabel, buildConfigurableAxisLabel } from '../../utils/chart.utils';

const ecStat: any = ecStatRaw;

@Component({
  selector: 'app-quad-bubble-chart',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgxEchartsModule,
    CommonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './quad-bubble-chart.component.html',
  styleUrl: './quad-bubble-chart.component.scss'
})
export class QuadBubbleChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  protected responsiveLayoutService = inject(ResponsiveLayoutService);
  protected reflowSub?: Subscription;
  protected chart!: echarts.ECharts;

  ngAfterViewInit(): void {
    const chartDom = document.getElementById(this.data.widgetId);
    if (chartDom) {
      this.chart = echarts.init(chartDom);
      this.renderChart();
      window.addEventListener('resize', this.resizeChart);
    }

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.chart?.resize();
    });
  }

  protected renderChart(): void {
    echarts.registerTransform(ecStat.transform.clustering);

    const raw = this.resolvePath(this.data, this.data.series);
    const clusterCount = this.data.clusterCount || 4;
    const symbolSize = this.data.symbolSize || 15;

    const points: number[][] = [
      ...(raw?.q1 ?? []),
      ...(raw?.q2 ?? []),
      ...(raw?.q3 ?? []),
      ...(raw?.q4 ?? [])
    ];

    const dimensionClusterIndex = 2;

    const COLOR_ALL = [
      '#37A2DA', '#e06343', '#37a354',
      '#b55dba', '#b5bd48', '#8378EA', '#96BFFF'
    ];

    const option: echarts.EChartsOption = {
      dataset: [
        { source: points },
        {
          transform: {
            type: 'ecStat:clustering',
            config: {
              clusterCount,
              outputType: 'single',
              outputClusterIndexDimension: dimensionClusterIndex
            }
          }
        }
      ],
      tooltip: {
        trigger: 'item',
        appendToBody: true,
        formatter: (() => {
          const tooltipCfg = this.data?.chartConfig?.tooltip;
          if (tooltipCfg?.fields?.length) {
            return (params: any) => {
              const d = params.data;
              const lines: string[] = [];
              for (const field of tooltipCfg.fields) {
                const label = getTooltipLabel(field, tooltipCfg.autoCapitalizeKeys !== false);
                const idx = parseInt(field.key, 10);
                const rawValue = !isNaN(idx) ? d[idx] : d[field.key];
                lines.push(`<b>${label}:</b> ${formatTooltipValue(rawValue, field)}`);
              }
              return lines.join('<br/>');
            };
          }
          return (params: any) => `x: ${params.data[0]}, y: ${params.data[1]}`;
        })()
      },
      visualMap: {
        type: 'piecewise',
        dimension: dimensionClusterIndex,
        selectedMode: 'multiple',
        pieces: Array.from({ length: clusterCount }, (_, i) => ({
          value: i,
          label: `Cluster ${i}`,
          color: COLOR_ALL[i]
        }))
      },
      xAxis: {
        min: this.data.xAxis?.min ?? 0,
        max: this.data.xAxis?.max ?? 10,
        splitLine: { show: false }
      },
      yAxis: {
        min: this.data.yAxis?.min ?? 0,
        max: this.data.yAxis?.max ?? 10,
        splitLine: { show: false },
        ...( buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis)
          ? { axisLabel: buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis) }
          : {}),
      },
      grid: { left: '2%', right: '4%', top: '10%', bottom: '15%', containLabel: true },
      series: [
        {
          type: 'scatter',
          datasetIndex: 1,
          encode: { tooltip: [0, 1] },
          symbolSize,
          itemStyle: {
            borderColor: 'transparent'
          },
          markArea: {
            silent: true,
            itemStyle: { opacity: 0.2 },
            data: this.getQuadrantAreas()
          }
        }
      ]
    };

    this.chart.setOption(option);
  }

  protected getQuadrantAreas(): any[] {
    return (this.data.quadrantBackgrounds || []).map((quad: any) => ([
      {
        name: '',
        xAxis: quad.xAxis,
        yAxis: quad.yAxis,
        itemStyle: { color: quad.color }
      },
      {
        xAxis: quad.x2,
        yAxis: quad.y2
      }
    ]));
  }

  protected resolvePath(obj: any, path: string): any {
    return path.split('.').reduce((o, key) => (o ? o[key] : undefined), obj);
  }

  protected resizeChart = () => {
    this.chart?.resize();
  };

  ngOnDestroy(): void {
    this.reflowSub?.unsubscribe();
    window.removeEventListener('resize', this.resizeChart);
    this.chart?.dispose();
  }
}